// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.
import { Cookies } from 'react-cookie';
import type { SessionUser } from '@/types/user';

export const SESSION_EXPIRY_PERIOD = 180 * 24 * 60 * 60; //  180 days - 6 months. Must match /lib/auth.js, routes/auth/auth.js
const MAX_BYTES = 4000; // Should not exceed 4096 bytes (RFC 6265)

export const setSessionToken = (token: string) => {
  const cookie = new Cookies();

  cookie.set('session_token', token, {
    path: '/',
    secure: true,
    sameSite: 'strict',
    maxAge: SESSION_EXPIRY_PERIOD, // 6 months 180 days
  });
};

export const setAuthCookies = (
  accessToken: string,
  sessionToken: string,
  refreshToken: string,
  userData: SessionUser | null
) => {
  const cookie = new Cookies();

  // Set HTTP-only cookie (handled by server)
  // For client-side accessible cookies (use sparingly)
  cookie.set('access_token', accessToken, {
    path: '/',
    secure: true,
    sameSite: 'strict',
    maxAge: 15 * 60 * 1000, // 15 minutes
  });

  cookie.set('session_token', sessionToken, {
    path: '/',
    secure: true,
    sameSite: 'strict',
    maxAge: SESSION_EXPIRY_PERIOD,
  });

  cookie.set('refresh_token', refreshToken, {
    path: '/',
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'strict',
    maxAge: 7 * 24 * 60 * 60,
  });

  if (userData) {
    const basePayload = {
      id: userData.id,
      name: userData.name,
      phone: userData.phone,
      email: userData.email,
    };
    let payload = { ...basePayload, image: userData.image };
    let json = JSON.stringify(payload);

    if (new TextEncoder().encode(json).length > MAX_BYTES) {
      let payload = { ...basePayload };
      json = JSON.stringify(payload);
    }
    // role: userData.role,   // no role at this step
    // isOAuth: userData.isOAuth,
    // isTwoFactorEnabled: userData.isTwoFactorEnabled,   // not get from express /lib/auth.js
    // WARNING: Should not exceed 4096 bytes (RFC 6265)
    cookie.set('user_data', json, {
      path: '/',
      secure: true,
      sameSite: 'strict',
      maxAge: SESSION_EXPIRY_PERIOD,
    });
    cookie.set('user_data', json, {
      path: '/',
      secure: true,
      sameSite: 'strict',
      maxAge: SESSION_EXPIRY_PERIOD,
    });
  }
};

export const clearAuthCookies = () => {
  const cookie = new Cookies();
  cookie.remove('auth_token');
  cookie.remove('user_data');
};

export const clearAllClientCookies = () => {
  const cookie = new Cookies();

  const commonOptions = {
    path: '/',
    secure: true,
    sameSite: 'strict' as const,
  };

  [
    'access_token',
    'session_token',
    'refresh_token',
    'user_data',
    'csrf_token',
    'connect.sid',
  ].forEach((name) => {
    cookie.remove(name, commonOptions);
  });
};

// To force remove all cookies, you must also clear them on the server
export const clearCookiesAndStorage = () => {
  try {
    clearAllClientCookies();
    localStorage.clear();
    sessionStorage.clear();
  } catch (error) {
    console.log(error);
  }
};

export const getAuthToken = () => {
  const cookie = new Cookies();
  return cookie.get('auth_token');
};

export const getUserData = (): SessionUser | null => {
  try {
    const cookies = new Cookies();
    const userData = cookies.get('user_data');

    if (!userData) return null;

    if (typeof userData === 'object') {
      return userData as SessionUser;
    }

    if (typeof userData === 'string') {
      const parsed = JSON.parse(userData);
      return parsed as SessionUser;
    }

    return null;
  } catch (error) {
    console.error('Failed to get user data:', error);
    new Cookies().remove('user_data');
    return null;
  }
};
